import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import * as cadQuoteApi from '../../lib/api/cadQuote';
import AskDialog from '../common/dialog/AskDialog';
import CadQuoteSavedQuoteDialog from './CadQuoteSavedQuoteDialog';

const formatWon = (value) => (value == null ? '-' : `${Math.round(Number(value) || 0).toLocaleString('ko-KR')}원`);

/**
 * "저장된 견적" 탭 - 키워드(품번/파일명) 검색, 실제 견적가 입력/수정(행 안에서 바로),
 * 삭제(AskDialog로 인페이지 확인, window.confirm 안 씀), 행 클릭 시 읽기전용 상세.
 */
const CadQuoteHistoryList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [keyword, setKeyword] = useState('');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);
  const [editingAmt, setEditingAmt] = useState('');
  const [deleteTargetUuid, setDeleteTargetUuid] = useState(null);
  const [detailUuid, setDetailUuid] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    cadQuoteApi.listCadQuotes({ page, size, keyword })
      .then((res) => {
        setRows(res?.data?.list || []);
        setTotalCount(res?.data?.totalCount || 0);
      })
      .catch(() => enqueueSnackbar('저장된 견적 목록을 불러오지 못했습니다.', { variant: 'error' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, keyword]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStartEdit = (row) => {
    setEditingUuid(row.qtUuid);
    setEditingAmt(row.finalAmt != null ? String(row.finalAmt) : '');
  };

  const handleSaveEdit = async (row) => {
    try {
      await cadQuoteApi.patchCadQuote(row.qtUuid, Number(editingAmt) || 0, row.memo || '');
      enqueueSnackbar('실제 견적가를 저장했습니다.', { variant: 'success' });
      setEditingUuid(null);
      load();
    } catch (err) {
      enqueueSnackbar('실제 견적가 저장에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    const target = deleteTargetUuid;
    setDeleteTargetUuid(null);
    if (!target) return;
    try {
      await cadQuoteApi.deleteCadQuote(target);
      enqueueSnackbar('삭제했습니다.', { variant: 'success' });
      load();
    } catch (err) {
      enqueueSnackbar('삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">저장된 견적</Typography>
        <TextField
          size="small"
          placeholder="품번/파일명 검색"
          value={keyword}
          onChange={(e) => { setPage(0); setKeyword(e.target.value); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>품번</TableCell>
              <TableCell>파일명</TableCell>
              <TableCell>재질</TableCell>
              <TableCell align="right">부품/레이어</TableCell>
              <TableCell align="right">견적 합계</TableCell>
              <TableCell align="right">실제 견적가</TableCell>
              <TableCell>등록</TableCell>
              <TableCell align="center">삭제</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.qtUuid} hover>
                <TableCell sx={{ cursor: 'pointer' }} onClick={() => setDetailUuid(row.qtUuid)}>{row.partNo || '-'}</TableCell>
                <TableCell sx={{ cursor: 'pointer' }} onClick={() => setDetailUuid(row.qtUuid)}>{row.fileNm}</TableCell>
                <TableCell>{row.mainMtrlCd || '-'}</TableCell>
                <TableCell align="right">{row.partCnt ?? '-'}/{row.layerCnt ?? '-'}</TableCell>
                <TableCell align="right">{formatWon(row.totalAmt)}</TableCell>
                <TableCell align="right">
                  {editingUuid === row.qtUuid ? (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <TextField
                        type="number"
                        size="small"
                        value={editingAmt}
                        onChange={(e) => setEditingAmt(e.target.value)}
                        sx={{ width: 110 }}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                      <Button size="small" onClick={() => handleSaveEdit(row)}>저장</Button>
                    </Stack>
                  ) : (
                    <Box sx={{ cursor: 'pointer' }} onClick={() => handleStartEdit(row)} title="클릭해서 입력/수정">
                      {row.finalAmt != null ? formatWon(row.finalAmt) : '입력'}
                    </Box>
                  )}
                </TableCell>
                <TableCell>{row.regNm} / {row.regDt}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => setDeleteTargetUuid(row.qtUuid)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">저장된 견적이 없습니다.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={size}
        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
      />

      <AskDialog
        visible={Boolean(deleteTargetUuid)}
        title="저장된 견적 삭제"
        description="선택한 견적을 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={handleDelete}
        onCancle={() => setDeleteTargetUuid(null)}
      />

      <CadQuoteSavedQuoteDialog open={Boolean(detailUuid)} qtUuid={detailUuid} onClose={() => setDetailUuid(null)} />
    </Paper>
  );
};

export default CadQuoteHistoryList;
